import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import { Loader } from 'components/Widget/Loader';
import useDsLinkState, { DsLinkState } from 'hooks/useDsLinkState';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { selectActiveScene, selectActiveSceneDsLink } from 'redux/db/reducers';
import { canEdit } from 'redux/db/util';
import { dismissDsBanner } from 'redux/mode/actions';
import { selectDismissedDsBanner } from 'redux/mode/reducers';
import { displayViewOnlyModal } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import * as routes from 'routes';
import * as S from './styles';

type BannerState = DsLinkState.DRAFT_EXISTS | DsLinkState.PUBLISHING | DsLinkState.STALE;

const BANNER_CONFIG: Record<BannerState, { title: string; actionLabel?: string }> = {
	[DsLinkState.DRAFT_EXISTS]: {
		title: 'Unpublished design system changes',
		actionLabel: 'Edit',
	},
	[DsLinkState.PUBLISHING]: {
		title: 'Preparing design system update…',
	},
	[DsLinkState.STALE]: {
		title: 'Design system update available',
		actionLabel: 'Upgrade',
	},
};

const DsStatusBanner = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const dsLinkState = useDsLinkState();

	const { scene, dsLink, dismissedState, userOwnsDs } = useSelector((r: RootReducer) => {
		const link = selectActiveSceneDsLink(r.db);
		return {
			scene: selectActiveScene(r.db, r.mode.activeSceneId),
			dsLink: link,
			dismissedState: selectDismissedDsBanner(
				r.mode,
				link?.designSystem.id,
				link?.latestPublishedVersion.versionNumber
			),
			userOwnsDs: !!link && r.db.designSystems.some((ds) => ds.id === link.designSystem.id),
		};
	}, shallowEqual);

	if (dsLinkState === DsLinkState.FRESH) return null;
	if (dismissedState === dsLinkState) return null;
	if (dsLinkState === DsLinkState.DRAFT_EXISTS && !userOwnsDs) return null;

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------
	const onDismiss = () => {
		if (dsLink) {
			dispatch(
				dismissDsBanner({
					dsId: dsLink.designSystem.id,
					dismissedState: dsLinkState,
					versionNumber: dsLink.latestPublishedVersion.versionNumber,
				})
			);
		}
	};

	const onUpgrade = async () => {
		if (!scene || !dsLink) return;
		if (!canEdit(scene)) {
			displayViewOnlyModal();
			return;
		}
		try {
			await forge.linkSceneDesignSystem({
				sceneId: scene.id,
				designSystemId: dsLink.designSystem.id,
			});
		} catch (error) {
			console.error('Failed to upgrade design system:', error);
			toast.error('Failed to upgrade design system. Please try again.');
		}
	};

	const onNavigateToDs = () => {
		if (!dsLink) return;
		navigate(routes.designSystem(dsLink.designSystem.id, dsLink.designSystem.title));
	};

	const onAction = () => {
		if (dsLinkState === DsLinkState.STALE) {
			onUpgrade();
		} else {
			onNavigateToDs();
		}
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------
	const config = BANNER_CONFIG[dsLinkState];
	const title =
		dsLinkState === DsLinkState.STALE && dsLink?.latestPublishedVersion.versionNumber
			? `Design system v${dsLink.latestPublishedVersion.versionNumber} available`
			: config.title;

	return (
		<S.Base data-testid="ds-status-banner" data-ds-link-state={dsLinkState}>
			<S.Header>
				<S.Title>
					{dsLinkState === DsLinkState.PUBLISHING && <Loader size={12} />}
					{title}
					{config.actionLabel && (
						<>
							{' · '}
							<S.ActionLink onClick={onAction} data-testid="ds-status-banner-action">
								{config.actionLabel}
							</S.ActionLink>
						</>
					)}
				</S.Title>
				<S.CloseButton onClick={onDismiss} aria-label="Dismiss">
					<Icon type={IconType.CLOSE} width={10} height={10} />
				</S.CloseButton>
			</S.Header>
		</S.Base>
	);
};

export default DsStatusBanner;
